/**
 * ProsConsWorkspace — column-per-site pros/cons surface that replaces the
 * legacy single-column EvaluationProsCons tab.
 *
 *   ┌────────────┐ ┌────────────┐ ┌────────────┐
 *   │ Site A     │ │ Site B     │ │ Site C     │   one column per site
 *   │ ┌────────┐ │ │ ┌────────┐ │ │ ┌────────┐ │
 *   │ │ Pros   │ │ │ │ Pros   │ │ │ │ Pros   │ │
 *   │ ├────────┤ │ │ ├────────┤ │ │ ├────────┤ │
 *   │ │ Cons   │ │ │ │ Cons   │ │ │ │ Cons   │ │
 *   │ └────────┘ │ │ └────────┘ │ │ └────────┘ │
 *   └────────────┘ └────────────┘ └────────────┘
 *
 *   < 4 sites and ≥ lg: row of equal-flex columns inside the available
 *                       width (no horizontal scroll).
 *   ≥ 4 sites OR < lg: horizontal-scroll strip of fixed-width 320px
 *                      columns. Mobile-friendly.
 *
 * Sort within each panel:
 *   - pros desc by upvotes
 *   - cons desc by impact level (HIGH > MEDIUM > LOW > unset)
 *
 * One shared ProConFormModal is owned here. All columns dispatch through
 * onAdd / onEdit / onDelete / onVote handlers, and the modal close hook
 * refreshes the cross-site comparison summary.
 *
 * Linear: MYD-223 (H1).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { ListChecks, Building2, CheckCircle2, XCircle, LayoutTemplate, Save } from 'lucide-react'
import { fetchSites } from 'slices/evaluationSiteSlice'
import {
	fetchSiteProsCons,
	deleteProCon,
	voteProCon,
	fetchProsConsComparison,
} from 'slices/proConSlice'
import ProsConsSiteColumn from './ProsConsSiteColumn'
import ProConFormModal from './ProConFormModal'
import ApplyTemplateModalCN from '../templates/ApplyTemplateModalCN'
import SaveAsTemplateModalCN from '../templates/SaveAsTemplateModalCN'

interface ProsConsWorkspaceProps {
	evalId: string
}

const ProsConsWorkspace = ({ evalId }: ProsConsWorkspaceProps) => {
	const dispatch = useDispatch()

	const { entities: siteEntities, byEvaluation } = useSelector(
		(state: any) => state.evaluationSites,
	)
	const { bySite, comparisonByEvaluation } = useSelector((state: any) => state.proCons)

	const sites: any[] = useMemo(() => {
		const ids: string[] = byEvaluation[evalId] || []
		return ids.map((id) => siteEntities[id]).filter(Boolean)
	}, [byEvaluation, siteEntities, evalId])

	const [formOpen, setFormOpen] = useState(false)
	const [formSiteId, setFormSiteId] = useState('')
	const [formItem, setFormItem] = useState<any>(null)
	const [formDefaultType, setFormDefaultType] = useState<'PRO' | 'CON'>('PRO')

	const [applyOpen, setApplyOpen] = useState(false)
	const [saveAsOpen, setSaveAsOpen] = useState(false)

	// Snapshot for Save-As: collect items from across all sites and dedupe by
	// `description + type`. After applying a template to multiple sites and
	// editing some, this captures the curated set. Pairs of same description
	// across PRO+CON are kept distinct because they're semantically different.
	const sourceItems = useMemo(() => {
		const seen: Record<string, any> = {}
		for (const siteId of Object.keys(bySite)) {
			for (const item of bySite[siteId] || []) {
				if (!item.description || !item.type) continue
				const key = `${item.type}::${item.description}`
				if (!seen[key]) {
					seen[key] = {
						type: item.type,
						category: item.category,
						description: item.description,
						impactLevel: item.impactLevel,
					}
				}
			}
		}
		return Object.values(seen)
	}, [bySite])

	const handleTemplateApplied = useCallback(() => {
		// @ts-ignore — thunk
		dispatch(fetchProsConsComparison(evalId))
		// Refresh per-site pros/cons so the columns hydrate with seeded data.
		sites.forEach((s) => {
			// @ts-ignore — thunk
			dispatch(fetchSiteProsCons({ evalId, siteId: s._id }))
		})
	}, [dispatch, evalId, sites])

	useEffect(() => {
		;(dispatch as any)(fetchSites(evalId))
		;(dispatch as any)(fetchProsConsComparison(evalId))
	}, [dispatch, evalId])

	// Fan out: load pros/cons for each site once we know the site list.
	useEffect(() => {
		sites.forEach((s) => {
			;(dispatch as any)(fetchSiteProsCons({ evalId, siteId: s._id }))
		})
	}, [dispatch, evalId, sites])

	const handleAdd = useCallback((siteId: string, type: 'PRO' | 'CON') => {
		setFormItem(null)
		setFormSiteId(siteId)
		setFormDefaultType(type)
		setFormOpen(true)
	}, [])

	const handleEdit = useCallback((item: any) => {
		setFormItem(item)
		setFormSiteId(typeof item.site === 'string' ? item.site : String(item.site))
		setFormDefaultType(item.type)
		setFormOpen(true)
	}, [])

	const handleDelete = useCallback(
		async (siteId: string, itemId: string) => {
			if (!(await toast.confirm({ title: 'Delete item', message: 'Delete this pro/con item?' })))
				return
			await (dispatch as any)(deleteProCon({ evalId, siteId, itemId }))
			;(dispatch as any)(fetchProsConsComparison(evalId))
		},
		[dispatch, evalId],
	)

	const handleVote = useCallback(
		(siteId: string, itemId: string) => {
			;(dispatch as any)(voteProCon({ evalId, siteId, itemId }))
		},
		[dispatch, evalId],
	)

	const handleFormClose = useCallback(() => {
		setFormOpen(false)
		setFormItem(null)
		setFormSiteId('')
	}, [])

	if (sites.length === 0) {
		return (
			<div className="text-center py-12">
				<Building2 className="mx-auto h-12 w-12 text-gray-300 mb-3" />
				<p className="text-sm text-gray-500">
					No sites yet. Add sites in the Sites tab before tracking pros and cons.
				</p>
			</div>
		)
	}

	const comparison: any[] = comparisonByEvaluation[evalId] || []
	// Use a horizontal-scroll strip when there are 4+ sites (or always on
	// narrow viewports). lg+ with ≤ 3 sites gets the equal-flex row.
	const useScrollStrip = sites.length >= 4

	return (
		<div className="space-y-4">
			{/* Header */}
			<div className="flex flex-row items-start justify-between gap-3">
				<div>
					<h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
						<ListChecks className="w-5 h-5 text-[#0080fe]" />
						Pros &amp; Cons
					</h3>
					<p className="text-sm text-gray-500">
						One column per site — pros on top, cons on the bottom — to balance qualitative
						signal across the comparison.
					</p>
				</div>
				<div className="flex items-center gap-1.5 shrink-0">
					<Button
						variant="primary"
						isOutline
						onClick={() => setApplyOpen(true)}
						className="gap-1.5"
						title="Apply a pros/cons template across this evaluation's sites"
						aria-label="Apply pros/cons template"
					>
						<LayoutTemplate className="w-3.5 h-3.5" />
					</Button>
					<Button
						variant="primary"
						isOutline
						onClick={() => setSaveAsOpen(true)}
						disabled={sourceItems.length === 0}
						className="gap-1.5"
						title={
							sourceItems.length === 0
								? 'Add at least one pro or con to a site before snapshotting'
								: 'Save the curated pros/cons set as a reusable template'
						}
						aria-label="Save as pros/cons template"
					>
						<Save className="w-3.5 h-3.5" />
					</Button>
				</div>
			</div>

			{/* Columns */}
			<div
				className={
					useScrollStrip
						? 'flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 lg:mx-0 lg:px-0'
						: 'flex gap-3 overflow-x-auto lg:overflow-x-visible pb-2 -mx-1 px-1 lg:mx-0 lg:px-0'
				}
			>
				{sites.map((site) => (
					<ProsConsSiteColumn
						key={site._id}
						site={site}
						items={bySite[site._id] || []}
						onAdd={handleAdd}
						onEdit={handleEdit}
						onDelete={handleDelete}
						onVote={handleVote}
					/>
				))}
			</div>

			{/* Cross-site summary */}
			{comparison.length > 1 && (
				<Card>
					<CardHeader className="pb-3">
						<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
							Cross-site Pros / Cons Summary
						</h4>
						<p className="text-xs text-gray-500 mt-1">
							Pro and con counts per site (higher pro count + lower con count = stronger
							qualitative signal).
						</p>
					</CardHeader>
					<CardContent>
						<div className="space-y-1.5">
							{comparison
								.slice()
								.sort(
									(a: any, b: any) =>
										(b.proCount ?? 0) -
										(b.conCount ?? 0) -
										((a.proCount ?? 0) - (a.conCount ?? 0)),
								)
								.map((row: any) => (
									<div
										key={row.siteId}
										className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-gray-50"
									>
										<span className="text-sm font-medium text-gray-800">{row.siteName}</span>
										<div className="flex items-center gap-2">
											<Badge className="text-xs bg-green-100 text-green-700 gap-1">
												<CheckCircle2 className="w-3 h-3" />
												{row.proCount ?? 0}
											</Badge>
											<Badge className="text-xs bg-red-100 text-red-700 gap-1">
												<XCircle className="w-3 h-3" />
												{row.conCount ?? 0}
											</Badge>
										</div>
									</div>
								))}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Shared modal */}
			<ProConFormModal
				open={formOpen}
				onClose={handleFormClose}
				evalId={evalId}
				siteId={formSiteId}
				item={formItem}
				defaultType={formDefaultType}
			/>

			<ApplyTemplateModalCN
				open={applyOpen}
				onOpenChange={setApplyOpen}
				templateType="prosCons"
				evalId={evalId}
				onApplied={handleTemplateApplied}
			/>

			<SaveAsTemplateModalCN
				open={saveAsOpen}
				onOpenChange={setSaveAsOpen}
				templateType="prosCons"
				sourceItems={sourceItems}
			/>
		</div>
	)
}

export default ProsConsWorkspace
